import type { PublicationState } from '../../api';

const REVISION_LABEL = { baseline: 'Earlier published content', saved: 'Draft saved', published: 'Published', restored: 'Restored into draft', discarded: 'Draft discarded' };

export function savedDate(value: string): string {
  const normalized = value.replace(' ', 'T');
  const date = new Date(/T.*(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(normalized) ? normalized : `${normalized}Z`);
  return Number.isNaN(date.getTime()) ? 'time unavailable' : date.toLocaleString();
}

/** Shared authoring controls: only a saved draft can be published. */
export function DraftPublication({ version, publication, dirty, busy, conflict, invalid = false, preview, onPreview, onSave, onPublish, onDiscard, onReload, onRestore }: {
  version: number; publication: PublicationState; dirty: boolean; busy: boolean; conflict: boolean; invalid?: boolean;
  preview: boolean; onPreview: () => void; onSave: () => void; onPublish: () => void;
  onDiscard: () => void; onReload: () => void; onRestore: (revisionId: number) => void;
}) {
  const blocked = busy || conflict;
  return <>
    <p><strong>{publication.hasDraft ? 'Private draft saved' : 'Showing published content'}</strong> · Saved version {version} · Published version {publication.publishedVersion}</p>
    {publication.updatedAt && <p className="muted">Last saved: {savedDate(publication.updatedAt)}</p>}
    {dirty && <p className="notice">You have unsaved changes. Save the draft before previewing or publishing those changes.</p>}
    {conflict && <p className="notice notice--warn">Another author changed the saved version. Your edits are still in the form. Copy anything you want to keep, then reload the saved state before continuing.</p>}
    {publication.liveChanged && <p className="notice notice--warn">Published content changed since this draft started. Review the current published content, then discard this draft or restore a revision into a new draft before publishing.</p>}
    <div className="row" style={{ marginBlock: 16 }}>
      <button className="btn btn--primary" disabled={blocked || !dirty || invalid} onClick={onSave}>Save draft</button>
      <button className="btn btn--primary" disabled={blocked || dirty || !publication.hasDraft || publication.liveChanged} onClick={onPublish}>Publish saved draft</button>
      <button className="btn btn--ghost" disabled={busy} onClick={onPreview}>{preview ? 'Hide' : 'Show'} saved preview</button>
      <button className="btn btn--ghost" disabled={blocked || !publication.hasDraft} onClick={onDiscard}>Discard saved draft</button>
      <button className="btn btn--ghost" disabled={busy} onClick={onReload}>Reload saved state</button>
    </div>
    <details style={{ marginBlock: 16 }}>
      <summary>Revision history ({publication.revisions.length})</summary>
      <p className="muted">Restoring creates a private draft for you to check and publish. The latest 50 revisions are kept.</p>
      {publication.revisions.length === 0 ? <p className="muted">No saved revisions yet.</p> : <ol>
        {publication.revisions.map(revision => <li key={revision.id} style={{ marginBlock: 16 }}>
          <p><strong>Version {revision.version}</strong> · {REVISION_LABEL[revision.kind]} · {savedDate(revision.createdAt)}</p>
          <button className="btn btn--ghost" disabled={blocked} onClick={() => onRestore(revision.id)}>Restore version {revision.version} into draft</button>
        </li>)}
      </ol>}
    </details>
  </>;
}
